import React, { useEffect, useMemo, useState } from 'react';
import PettyCashStats from './PettyCashStats';
import PettyCashApprovals from './PettyCashApprovals';
import PettyCashAllocationHistory from './PettyCashAllocationHistory';
import { PettyCashApi } from 'api/petty/service';

const PettyCashDashboard = () => {
    const isOwner = localStorage.getItem("isOwner") === "true";
    const [approvals, setApprovals] = useState([]);
    const [approvalsLoading, setApprovalsLoading] = useState(false);
    const [approvalsError, setApprovalsError] = useState("");
    const [executeLoadingById, setExecuteLoadingById] = useState({});

    const [walletExists, setWalletExists] = useState(null); // null = unknown, boolean otherwise
    const [stats, setStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(false);
    const [history] = useState([]);

    const orgId = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem("organization") || "{}")?.id;
        } catch (_) {
            return null;
        }
    }, []);

    const loadWalletAndStats = async () => {
        if (!orgId) {
            setWalletExists(false);
            setStats(null);
            return;
        }

        setStatsLoading(true);
        try {
            const walletRes = await PettyCashApi.getWallet(orgId);
            const data = walletRes?.data ?? walletRes;
            const exists =
                (walletRes?.status ?? true) &&
                data != null &&
                (Array.isArray(data) ? data.length > 0 : Object.keys(data || {}).length > 0);

            setWalletExists(Boolean(exists));

            if (!exists) {
                setStats(null);
                return;
            }

            const overviewRes = await PettyCashApi.getOverview(orgId);
            const overview = overviewRes?.data ?? null;

            const totalPettyCash = typeof overview?.total_petty_cash === "number" ? overview.total_petty_cash : 0;
            const totalAllocated = typeof overview?.total_allocated === "number" ? overview.total_allocated : 0;
            const availableBalance = typeof overview?.total_balance_left === "number" ? overview.total_balance_left : 0;
            const totalSpent =
                typeof overview?.total_petty_cash === "number" && typeof overview?.total_balance_left === "number"
                    ? overview.total_petty_cash - overview.total_balance_left
                    : 0;

            setStats({
                availableBalance,
                totalSpent,
                totalAllocated,
                totalPettyCash,
            });
        } catch (err) {
            if (err?.status === 404) {
                setWalletExists(false);
                setStats(null);
            } else {
                console.error("Failed to load petty cash wallet/stats", err);
                setWalletExists(false);
                setStats(null);
            }
        } finally {
            setStatsLoading(false);
        }
    };

    const loadMyRequestedApprovals = async () => {
        if (!orgId || !walletExists || !isOwner) {
            setApprovals([]);
            setApprovalsError("");
            return;
        }

        setApprovalsLoading(true);
        setApprovalsError("");
        try {
            const res = await PettyCashApi.getMyRequestedApprovals(orgId);
            const payload = res?.data ?? res;
            const list = Array.isArray(payload?.data)
                ? payload.data
                : (Array.isArray(payload) ? payload : []);
            setApprovals(list);
        } catch (e) {
            console.error("Failed to load my requested approvals", e);
            setApprovals([]);
            setApprovalsError("Failed to load approvals. Please try again.");
        } finally {
            setApprovalsLoading(false);
        }
    };

    useEffect(() => {
        loadWalletAndStats();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId]);

    useEffect(() => {
        if (walletExists) loadMyRequestedApprovals();
        else {
            setApprovals([]);
            setApprovalsError("");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [walletExists, orgId, isOwner]);

    const handleExecutePayment = async (approvalId, category) => {
        if (!orgId || approvalId == null) return;

        setExecuteLoadingById((prev) => ({ ...prev, [approvalId]: true }));
        try {
            await PettyCashApi.executeTransactionsFromApproval(orgId, approvalId);

            setApprovals((prev) =>
                prev.map((item) => {
                    const id = item?.approval_id ?? item?.id;
                    return id === approvalId ? { ...item, is_executed: true } : item;
                })
            );
        } catch (e) {
            console.error("Failed to execute approval transaction", e);
            window.alert("Failed to execute. Please try again.");
        } finally {
            setExecuteLoadingById((prev) => {
                const next = { ...prev };
                delete next[approvalId];
                return next;
            });
        }
    };

    return (
        <div className="mt-2 mb-10">

            {statsLoading || walletExists === null ? (
                <div className="mb-4 text-sm text-gray-500">Loading petty cash...</div>
            ) : !walletExists ? (
                <div className="mb-4 text-sm text-gray-500">No petty cash wallet created yet.</div>
            ) : (
                <PettyCashStats stats={stats || { availableBalance: 0, totalSpent: 0, totalAllocated: 0 }} />
            )}

            {isOwner && walletExists && approvalsLoading && (
                <div className="mb-4 text-sm text-gray-500">Loading approvals...</div>
            )}
            {isOwner && walletExists && approvalsError && (
                <div className="mb-4 text-sm text-red-600">{approvalsError}</div>
            )}

            {isOwner && walletExists && (
                <PettyCashApprovals
                    approvals={approvals}
                    onExecute={handleExecutePayment}
                    executeLoadingById={executeLoadingById}
                />
            )}

            {walletExists && <PettyCashAllocationHistory history={history} />}
        </div>
    );
};

export default PettyCashDashboard;
